import {ReactElement, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {IStore} from "../redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { UsersApi } from "@devour/client";
import {useParams} from "react-router";
import {useMenuOrder} from "@/hooks/menuOrder/useMenuOrder";
import {toggleChatOpen} from "@/redux/meta/metaActions";

declare global {
    interface Window {
        Atlas: any;
    }
}

interface Props {
    chatbotId?: string;
    allowAnonymous?: boolean;
}

function AtlasChatWidget(props: Props): ReactElement {
    const dispatch = useDispatch();
    const {menuOrderId} = useParams<{ menuOrderId?: string }>();
    const {data: menuOrder} = useMenuOrder(menuOrderId);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isChatOpen = useSelector((store: IStore) => store.metaStore.isChatOpen);

    useEffect(() => {
        if (isChatOpen) {
            openChatWindow();
        }
    }, [isChatOpen]);

    useEffect(() => {
        registerMenuOrder();
    }, [menuOrder]);

    useEffect(() => {
        // Initialize Atlas for logged-in users, or if allowAnonymous is true
        if (!fullToken && !props.allowAnonymous) {
            return;
        }

        initAtlas();
        if (fullToken) {
            void identifyUser();
        }
    }, [fullToken?.id, props.allowAnonymous]);

    function initAtlas() {
        if (!window.Atlas) {
            try {
                let t;
                const e = {
                    appId: import.meta.env.VITE_ATLAS_ID,
                    v: 2,
                    q: [],
                    call: function () {
                        try {
                            // eslint-disable-next-line react/no-this-in-sfc,prefer-rest-params
                            this.q.push(arguments);
                        } catch (error) {
                            console.error("Error queuing Atlas call:", error);
                        }
                    },
                };
                window.Atlas = e;
                const n = document.createElement("script");
                n.async = !0, n.src = "https://app.atlas.so/client-js/atlas.bundle.js";

                // Add error handler for script loading
                n.onerror = (error) => {
                    console.error("Failed to load Atlas script:", error);
                };

                const s = document.getElementsByTagName("script")[0];
                (t = s.parentNode) === null || void 0 === t || t.insertBefore(n, s);

                window.Atlas.call("start", {
                    chat: {
                        hideBubble: true,
                        onWindowVisibilityChange: (visible: boolean) => {
                            if (!visible) {
                                // Set the redux state back to false
                                dispatch(toggleChatOpen(visible));
                            }
                        },
                    },
                });
            } catch (error) {
                console.error("Error initializing Atlas:", error);
            }
        }
    }

    function registerMenuOrder() {
        if (menuOrder &&
            typeof window.Atlas?.registerCallback === "function" &&
            typeof window.Atlas?.chat?.updateTicketFields === "function") {
            try {
                window.Atlas.registerCallback("onNewTicket", (ticket: any) => {
                    // Ensure ticket and ticketId exist before trying to update
                    if (ticket && ticket.ticketId) {
                        window.Atlas.chat.updateTicketFields(ticket.ticketId, {
                            devour_order_id: menuOrder.id || "",
                            handoff: menuOrder.handoff || "",
                            dsp_id: menuOrder.dspId || "",
                            first_delivery_id: menuOrder.deliveryId || "",
                        });
                    } else {
                        console.warn("Atlas registerMenuOrder: Invalid ticket data", ticket);
                    }
                });
            } catch (error) {
                console.error("Error registering menu order with Atlas:", error);
            }
        }
    }

    async function identifyUser(retryCount = 0): Promise<void> {
        // Check Atlas is fully initialized (has call function and internal q queue)
        const atlasReady = fullToken &&
            window.Atlas &&
            typeof window.Atlas.call === "function" &&
            (Array.isArray(window.Atlas.q) || window.Atlas._initialized);

        if (!atlasReady) {
            // Retry after Atlas script loads, max 5 retries
            if (retryCount < 5) {
                setTimeout(() => void identifyUser(retryCount + 1), 500);
            }
            return;
        }

        try {
            const res = await new UsersApi(getConfig(fullToken)).getUsersAtlasHash();

            // Re-check Atlas is still ready after async call
            if (!window.Atlas || typeof window.Atlas.call !== "function") {
                return;
            }

            // Ensure all required fields exist before calling Atlas
            if (res && res.userId && res.userHash) {
                // Final safety check - Atlas internal queue must exist
                if (!window.Atlas?.q && !window.Atlas?._initialized) {
                    return;
                }
                window.Atlas.call(
                    "identify",
                    {
                        userId: res.userId,
                        name: res.name || "",
                        email: res.email || "",
                        userHash: res.userHash,
                        customFields: {
                            role: "Diner",
                        },
                    },
                );
            }
        } catch {
            // Silently ignore Atlas errors - non-critical functionality
        }
    }

    function openChatWindow(): void {
        if (props.chatbotId && typeof window.Atlas?.call === "function") {
            window.Atlas.call("triggerChatbot", props.chatbotId);
        } else if (typeof window.Atlas?.chat?.openWindow === "function") {
            window.Atlas.chat?.openWindow();
        }
    }

    return null;
}

export default AtlasChatWidget;
